import React from 'react';
import PropTypes from 'prop-types';
import themeable from 'zignal-landscape/lib/themeable';

import Label from 'zignal-landscape/lib/Label';
import Exporter from '../Exporter';
import SourceCheckerDomainList from './components/SourceCheckerDomainList/';
import SourceCheckerMediaOutletList from './components/SourceCheckerMediaOutletList/';
import StoryCheckerResultList from './components/StoryCheckerResultList/';
import CheckboxResultFilter from './components/CheckboxResultFilter/';
import { filterHandler, storyFilterHandler } from './util/util';

const storyOrSourceTypeDescription = {
  content: {
    title: 'Story Type - Story Text',
    description: `Click on the dataset name to see taxonomy matches and to understand
      why a story was or was not covered.`
  },
  urls: {
    title: 'Story Type - URL',
    description: `Some of these stories are not covered by our platform.
      Click on the dataset names to see taxonomy matches and to understand why
      a story was or was not covered.`
  },
  domain: {
    title: 'Source Type - Domain or URL',
    description: `There are some sources that are not covered by our platform.
      It may be because the sources were not covered in the last 24 months.
      Please talk with the source providers for the ones that are not covered.`
  },
  mediaOutlet: {
    title: 'Media Outlet',
    description: 'Here is a list of media outlets that are covered by our platform.'
  },
};

const exportMappings = {
  sources: {
    domain: 'Domain',
    isCovered: 'Coverage Status',
    mediaOutlet: 'Media Outlet',
    authorGroupNames: 'Matching Groups',
  },
  stories: {
    url: 'Url',
    matchStatus: 'Match Status'
  }
};

// filter handler to return a flag indicating if a particular result is visible or not based on the current filter
const ResultList = ({
  styles,
  selectedCheckByOption,
  selectedSourceOrStoryType,
  results,
  profiles,
  filters,
  onFilterChange,
  onShowSimilarSources,
  onHideSimilarSources,
  sourceCheckerSimilars,
  showSourceCheckerSimilars,
  fetchingSimilarFor,
  hasGroups,
  campaignName,
  storyTextTitle,
  storyDetailHandler,
  selectedSource,
}) => {
  let component = null;
  switch (selectedSourceOrStoryType) {
    // sources type [mediaOutlet, domain] render strategy
    case 'domain' :
      component = (
        <SourceCheckerDomainList
          selectedSourceType={selectedSourceOrStoryType}
          results={results}
          filters={filters}
          filterHandler={filterHandler}
          styles={styles}
          hasGroups={hasGroups}
        />
      );
      break;
    case 'mediaOutlet' :
      component = (
        <SourceCheckerMediaOutletList
          selectedSourceType={selectedSourceOrStoryType}
          results={results}
          filters={filters}
          filterHandler={filterHandler}
          onShowSimilarSources={onShowSimilarSources}
          onHideSimilarSources={onHideSimilarSources}
          similarSources={sourceCheckerSimilars}
          showSourceCheckerSimilars={showSourceCheckerSimilars}
          fetchingSimilarFor={fetchingSimilarFor}
          styles={styles}
          hasGroups={hasGroups}
        />
      );
      break;
    // stories type [content, urls] render strategy
    case 'urls':
      component =  (
        <StoryCheckerResultList
          styles={styles}
          searchType="url"
          filters={filters}
          filterHandler={storyFilterHandler}
          profiles={profiles}
          results={results}
          campaignName={campaignName}
          detailHandler={storyDetailHandler}
          selectedSource={selectedSource}
        />
      );
      break;
    case 'content':
      component =  (
        <StoryCheckerResultList
          styles={styles}
          searchType="storyText"
          filters={filters}
          filterHandler={storyFilterHandler}
          profiles={profiles}
          results={results}
          campaignName={campaignName}
          storyTextTitle={storyTextTitle}
          detailHandler={storyDetailHandler}
          selectedSource={selectedSource}
        />
      );
      break;
    default:
      component = null;
      break;
  }
  const { description, title } = storyOrSourceTypeDescription[selectedSourceOrStoryType];
  // returning resulting strategy component for rendering

  const filterResults = (data, filterMethod) => {
    return data.filter((result) => {
      return filterMethod(filters, result);
    });
  };

  return (
    <div className={styles.listWrapper}>
      <div>
        <div className={styles.resultTitleContainer}>
          <div className={styles.resultTypeTitle}>{title}</div>
          {
            selectedCheckByOption === 'sources' &&
              <Exporter data={filterResults(results, filterHandler)} mapping={exportMappings[selectedCheckByOption]} />
          }
          {
            selectedCheckByOption === 'stories' && selectedSourceOrStoryType === 'urls' &&
              <Exporter
                data={filterResults(results, storyFilterHandler).map((r) => {
                  r.matchStatus = r.matchingProfileIds && r.matchingProfileIds.length > 0; return r;})}
                mapping={exportMappings[selectedCheckByOption]}
              />
          }
        </div>
        <p>{description}</p>
      </div>
      <div className={styles.filtersContainer}>
        {
          selectedSourceOrStoryType !== 'content' && (
            <div>
              <Label>Filter Results By</Label>
              <div className={styles.filtersWrapper}>
                <CheckboxResultFilter
                  id="covered"
                  type="positive"
                  label={selectedCheckByOption === 'sources' ? 'Covered' : 'Matches Dataset(s)'}
                  checked={filters.covered}
                  onChange={onFilterChange}
                />
                <CheckboxResultFilter
                  id="notCovered"
                  type="negative"
                  label={selectedCheckByOption === 'sources' ? 'Not Covered' : 'Does not match Dataset(s)'}
                  checked={filters.notCovered}
                  onChange={onFilterChange}
                />
                {
                  selectedSourceOrStoryType !== 'mediaOutlet' && (
                    <CheckboxResultFilter
                      id="invalid"
                      type="alert"
                      checked={filters.invalid}
                      onChange={onFilterChange}
                    />
                  )
                }
              </div>
            </div>
          )
        }
      </div>
      <div className={styles.resultListWrapper}>
        {component}
      </div>
    </div>
  );
};

ResultList.propTypes = {
  styles: PropTypes.object,
  selectedCheckByOption: PropTypes.string,
  selectedSourceOrStoryType: PropTypes.oneOf(['domain', 'mediaOutlet', 'urls', 'content']),
  results: PropTypes.array,
  profiles: PropTypes.array,
  similars: PropTypes.object,
  showCovered: PropTypes.bool,
  showNotCovered: PropTypes.bool,
  showInvalid: PropTypes.bool,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  onShowSimilarSources: PropTypes.func,
  onHideSimilarSources: PropTypes.func,
  sourceCheckerSimilars: PropTypes.object,
  showSourceCheckerSimilars: PropTypes.object,
  fetchingSimilarFor: PropTypes.string,
  campaignName: PropTypes.string,
  storyTextTitle: PropTypes.string,
  hasGroups: PropTypes.bool,
  storyDetailHandler: PropTypes.func,
  selectedSource: PropTypes.string
};

export default themeable(ResultList);
